/**
 * TileHighlight - renders tile highlighting for building placement mode
 * Shows green for valid placement, red for invalid
 */

import * as THREE from 'three';
import { getSurfaceY } from './TerrainGenerator';
import type { BuildingSize } from '@/types';

export interface HighlightColors {
  valid: number;
  invalid: number;
  hover: number;
}

const DEFAULT_COLORS: HighlightColors = {
  valid: 0x44ff44,   // Green - can build
  invalid: 0xff4444, // Red - cannot build
  hover: 0xffff44,   // Yellow - neutral hover
};

export class TileHighlight {
  private scene: THREE.Scene;
  private highlightGroup: THREE.Group;
  private highlightMeshes: THREE.Mesh[] = [];
  private edgeMeshes: THREE.LineSegments[] = [];
  private colors: HighlightColors;
  private visible: boolean = false;
  private pulseTime: number = 0;

  // Shared geometry for tiles
  private tileGeometry: THREE.PlaneGeometry;
  private tileMaterial: THREE.MeshBasicMaterial;
  private edgeGeometry: THREE.EdgesGeometry;
  private edgeMaterial: THREE.LineBasicMaterial;

  constructor(scene: THREE.Scene, colors: HighlightColors = DEFAULT_COLORS) {
    this.scene = scene;
    this.colors = colors;
    this.highlightGroup = new THREE.Group();
    this.highlightGroup.name = 'TileHighlight';
    this.scene.add(this.highlightGroup);

    // Create shared geometry (1x1 plane)
    this.tileGeometry = new THREE.PlaneGeometry(0.95, 0.95);
    this.tileGeometry.rotateX(-Math.PI / 2); // Lay flat

    this.tileMaterial = new THREE.MeshBasicMaterial({
      color: colors.valid,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    // Edge geometry for border
    const boxGeo = new THREE.BoxGeometry(0.98, 0.1, 0.98);
    this.edgeGeometry = new THREE.EdgesGeometry(boxGeo);
    boxGeo.dispose();

    this.edgeMaterial = new THREE.LineBasicMaterial({
      color: colors.valid,
      transparent: true,
      opacity: 0.8,
    });
  }

  /**
   * Update highlight based on mouse position and building size
   */
  updateHighlight(
    mouseWorldX: number,
    mouseWorldZ: number,
    buildingSize: BuildingSize | null,
    canPlace: boolean
  ): void {
    // Clear previous highlights
    this.clearHighlight();

    if (!buildingSize) {
      this.visible = false;
      return;
    }

    this.visible = true;

    // Calculate tile coordinates
    const baseTileX = Math.floor(mouseWorldX);
    const baseTileZ = Math.floor(mouseWorldZ);

    // Calculate offsets to center the building
    const offsetX = Math.floor(buildingSize.width / 2);
    const offsetZ = Math.floor(buildingSize.depth / 2);

    // Set color based on placement validity
    const color = canPlace ? this.colors.valid : this.colors.invalid;
    this.tileMaterial.color.setHex(color);
    this.edgeMaterial.color.setHex(color);

    // Create highlight for each tile
    for (let dz = 0; dz < buildingSize.depth; dz++) {
      for (let dx = 0; dx < buildingSize.width; dx++) {
        const tileX = baseTileX - offsetX + dx;
        const tileZ = baseTileZ - offsetZ + dz;

        // Get world position (tile center)
        const worldX = tileX + 0.5;
        const worldZ = tileZ + 0.5;
        const worldY = getSurfaceY(worldX, worldZ) + 0.05; // Slightly above terrain

        // Create highlight mesh
        const mesh = new THREE.Mesh(
          this.tileGeometry,
          this.tileMaterial.clone()
        );
        mesh.position.set(worldX, worldY, worldZ);
        mesh.renderOrder = 999; // Render on top
        this.highlightGroup.add(mesh);
        this.highlightMeshes.push(mesh);

        // Create edge mesh
        const edge = new THREE.LineSegments(
          this.edgeGeometry,
          this.edgeMaterial.clone()
        );
        edge.position.set(worldX, worldY, worldZ);
        edge.renderOrder = 1000;
        this.highlightGroup.add(edge);
        this.edgeMeshes.push(edge);
      }
    }
  }

  /**
   * Clear all highlight meshes
   */
  clearHighlight(): void {
    for (const mesh of this.highlightMeshes) {
      this.highlightGroup.remove(mesh);
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
    }
    this.highlightMeshes = [];

    for (const edge of this.edgeMeshes) {
      this.highlightGroup.remove(edge);
      if (edge.material instanceof THREE.Material) {
        edge.material.dispose();
      }
    }
    this.edgeMeshes = [];

    this.visible = false;
  }

  /**
   * Update animation (pulse effect for valid placement)
   */
  update(deltaTime: number): void {
    if (!this.visible || this.highlightMeshes.length === 0) return;

    this.pulseTime += deltaTime * 3;
    const pulse = 0.3 + Math.sin(this.pulseTime) * 0.15;

    for (const mesh of this.highlightMeshes) {
      if (mesh.material instanceof THREE.MeshBasicMaterial) {
        mesh.material.opacity = pulse;
      }
    }
  }

  /**
   * Get the center position of the current highlight
   */
  getHighlightCenter(): { x: number; z: number } | null {
    if (this.highlightMeshes.length === 0) return null;

    let sumX = 0;
    let sumZ = 0;

    for (const mesh of this.highlightMeshes) {
      sumX += mesh.position.x;
      sumZ += mesh.position.z;
    }

    return {
      x: sumX / this.highlightMeshes.length,
      z: sumZ / this.highlightMeshes.length,
    };
  }

  /**
   * Check if highlight is currently visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    this.clearHighlight();
    this.tileGeometry.dispose();
    this.tileMaterial.dispose();
    this.edgeGeometry.dispose();
    this.edgeMaterial.dispose();
    this.scene.remove(this.highlightGroup);
  }
}
